import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';

import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { resolveApiMessage, showToast } from '../../../helpers';
import { downloadBlob } from '../../../helpers/downloadHelper';
import {
  exportMyRegistrations,
  getMyRegistrations,
  getMyRegistrationsAttendance,
  RegFilters,
  cancelMyRegistration,
} from '../../../requests/customerPortal';
import {
  btnGhost,
  btnSecondary,
  inputClass,
  tableClass,
  tdClass,
  thClass,
  theadClass,
  Card,
  EmptyState,
  PageBody,
  PageHeader,
  Pagination,
  Pill,
  Toolbar,
} from '../../Admin/ui';
import {
  dateInputClass,
  fmtDateTime,
  fmtNum,
  Stat,
  LoadFailed,
  retryUnless4xx,
} from '../shared';

const STATUSES = [
  { value: '', label: 'All statuses' },
  { value: 'Booked', label: 'Booked — signed up for a class in the future' },
  { value: 'Completed', label: 'Completed — attended the class' },
  { value: 'No Attend', label: 'No Attend — did not show up' },
  { value: 'Cancelled', label: 'Cancelled — let us know ahead of time' },
];

const RANGES: {
  value: string;
  label: string;
  from: () => string;
  to: () => string;
}[] = (() => {
  const d = (x: Date) => x.toISOString().slice(0, 10);
  const now = new Date();
  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - now.getDay());
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);
  const nextWeekStart = new Date(endOfWeek);
  nextWeekStart.setDate(endOfWeek.getDate() + 1);
  const nextWeekEnd = new Date(nextWeekStart);
  nextWeekEnd.setDate(nextWeekStart.getDate() + 6);
  const y = now.getFullYear();
  const m = now.getMonth();
  return [
    { value: '', label: 'Any date', from: () => '', to: () => '' },
    {
      value: 'week',
      label: 'This week',
      from: () => d(startOfWeek),
      to: () => d(endOfWeek),
    },
    {
      value: 'nextweek',
      label: 'Next week',
      from: () => d(nextWeekStart),
      to: () => d(nextWeekEnd),
    },
    {
      value: 'month',
      label: 'This month',
      from: () => d(new Date(y, m, 1)),
      to: () => d(new Date(y, m + 1, 0)),
    },
    {
      value: 'year',
      label: 'This year',
      from: () => `${y}-01-01`,
      to: () => `${y}-12-31`,
    },
    {
      value: 'lastyear',
      label: 'Last year',
      from: () => `${y - 1}-01-01`,
      to: () => `${y - 1}-12-31`,
    },
    { value: 'custom', label: 'Custom range…', from: () => '', to: () => '' },
  ];
})();

const AdminRegistrations: React.FC = () => {
  const [status, setStatus] = useState('');
  const qc = useQueryClient();
  const release = useMutation({
    mutationFn: (regId: string) => cancelMyRegistration(regId),
    onSuccess: () => {
      showToast('Seat released', 'success');
      qc.invalidateQueries({ queryKey: ['my', 'registrations'] });
      qc.invalidateQueries({ queryKey: ['my', 'events'] });
    },
    onError: (err) => showToast(resolveApiMessage(err), 'error'),
  });
  const [category, setCategory] = useState('');
  const [range, setRange] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      setQ(search.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const pickRange = (v: string) => {
    setRange(v);
    const r = RANGES.find((x) => x.value === v);
    if (r && v !== 'custom') {
      setFrom(r.from());
      setTo(r.to());
    }
    setPage(1);
  };

  const filters: RegFilters = { status, category, from, to, q, page };
  const {
    data,
    isPending: isLoading,
    error,
  } = useQuery({
    retry: retryUnless4xx,
    queryKey: ['my', 'registrations', filters],
    queryFn: () => getMyRegistrations(filters),
    placeholderData: (prev) => prev,
  });

  const [showAttendance, setShowAttendance] = useState(false);
  const { data: attendance } = useQuery({
    retry: retryUnless4xx,
    queryKey: ['my', 'registrations', 'attendance', from, to],
    queryFn: () => getMyRegistrationsAttendance({ from, to }),
    enabled: showAttendance,
  });

  const onExport = async () => {
    setExporting(true);
    try {
      const blob = await exportMyRegistrations({
        status,
        category,
        from,
        to,
        q,
      });
      downloadBlob(blob, 'registrations.csv');
    } catch {
      showToast('Export failed', 'error');
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Registrations"
        meta="Who is enrolled in what. Filter by status, date or category, then export the rows for Excel."
        actions={
          <button
            className={btnSecondary}
            onClick={onExport}
            disabled={exporting}
          >
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        }
      />
      <PageBody>
        <Toolbar>
          <select
            className={inputClass}
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
          >
            {STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
          <select
            className={inputClass}
            value={range}
            onChange={(e) => pickRange(e.target.value)}
          >
            {RANGES.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
          {range === 'custom' && (
            <>
              <input
                type="date"
                className={dateInputClass}
                value={from}
                onChange={(e) => {
                  setFrom(e.target.value);
                  setPage(1);
                }}
              />
              <input
                type="date"
                className={dateInputClass}
                value={to}
                onChange={(e) => {
                  setTo(e.target.value);
                  setPage(1);
                }}
              />
            </>
          )}
          <select
            className={inputClass}
            value={category}
            onChange={(e) => {
              setCategory(e.target.value);
              setPage(1);
            }}
          >
            <option value="">Public and private</option>
            <option value="Public">Public classes</option>
            <option value="Private">Private classes</option>
          </select>
          <input
            className={inputClass}
            placeholder="Search name, email or course"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Toolbar>

        {isLoading ? (
          <LoadingSpinner />
        ) : error || !data ? (
          <LoadFailed error={error} />
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <Stat
                label="Registrations"
                value={fmtNum(data.total)}
                caption="matching these filters"
              />
              <Stat
                label="Training hours"
                value={fmtNum(data.trainingHours)}
                caption="from completed classes"
              />
              <div className="rounded-xl border border-white-regular bg-white px-5 py-4">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-two">
                  Attendance
                </div>
                <ul className="mt-1 space-y-0.5 text-sm">
                  {data.byStatus.slice(0, 4).map((s) => (
                    <li key={s.status} className="flex justify-between">
                      <span className="text-gray-three">{s.status}</span>
                      <span className="font-medium text-gray-five">
                        {fmtNum(s.count)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-xl border border-white-regular bg-white px-5 py-4">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-two">
                  Popular classes
                </div>
                <ul className="mt-1 space-y-0.5 text-sm">
                  {data.popular.slice(0, 4).map((c) => (
                    <li key={c.course} className="flex justify-between gap-2">
                      <span
                        className="truncate text-gray-three"
                        title={c.course}
                      >
                        {c.course}
                      </span>
                      <span className="font-medium text-gray-five">
                        {fmtNum(c.count)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <Card
              className="mt-6"
              title="Attendance by class"
              right={
                <button
                  className={btnGhost}
                  onClick={() => setShowAttendance((v) => !v)}
                >
                  {showAttendance ? 'Hide' : 'Show'}
                </button>
              }
            >
              {!showAttendance ? (
                <div className="px-5 py-3 text-xs text-gray-bold">
                  Attended registrations as a share of the seats held on each
                  class, for the date range above.
                </div>
              ) : !attendance ? (
                <LoadingSpinner />
              ) : attendance.items.length === 0 ? (
                <EmptyState
                  title="No classes in this range"
                  hint="Widen the date range to see attendance by class."
                />
              ) : (
                <>
                  <div className="flex flex-wrap gap-8 border-b border-white-three px-5 py-3 text-sm">
                    <div>
                      <span className="text-gray-three">Classes run</span>{' '}
                      <span className="font-semibold text-gray-five">
                        {fmtNum(attendance.overall.classes)}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-three">Registrations</span>{' '}
                      <span className="font-semibold text-gray-five">
                        {fmtNum(attendance.overall.registrations)}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-three">Attended</span>{' '}
                      <span className="font-semibold text-gray-five">
                        {fmtNum(attendance.overall.attended)}
                        {attendance.overall.attendancePct != null
                          ? ` (${attendance.overall.attendancePct}%)`
                          : ''}
                      </span>
                    </div>
                  </div>
                  <table className={tableClass}>
                    <thead className={theadClass}>
                      <tr>
                        <th className={thClass}>Class</th>
                        <th className={thClass}>Start</th>
                        <th className={thClass}>Category</th>
                        <th className={`${thClass} text-right`}>
                          Registrations
                        </th>
                        <th className={`${thClass} text-right`}>Attended</th>
                        <th className={`${thClass} text-right`}>No attend</th>
                        <th className={`${thClass} text-right`}>Attendance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attendance.items.map((c) => (
                        <tr key={c.key}>
                          <td className={tdClass}>{c.course ?? '—'}</td>
                          <td className={`${tdClass} whitespace-nowrap`}>
                            {fmtDateTime(c.start)}
                          </td>
                          <td className={tdClass}>{c.category ?? '—'}</td>
                          <td className={`${tdClass} text-right`}>
                            {fmtNum(c.registrations)}
                          </td>
                          <td className={`${tdClass} text-right`}>
                            {fmtNum(c.attended)}
                          </td>
                          <td className={`${tdClass} text-right`}>
                            {fmtNum(c.noAttend)}
                          </td>
                          <td className={`${tdClass} text-right font-semibold`}>
                            {c.attendancePct == null
                              ? 'Not yet run'
                              : `${c.attendancePct}%`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </Card>

            <Card className="mt-6">
              {data.items.length === 0 ? (
                <EmptyState
                  title="No registrations match"
                  hint="Try widening the date range or status."
                />
              ) : (
                <table className={tableClass}>
                  <thead className={theadClass}>
                    <tr>
                      <th className={thClass}>Registered course</th>
                      <th className={thClass}>Reg. no.</th>
                      <th className={thClass}>Category</th>
                      <th className={thClass}>Course start</th>
                      <th className={thClass}>Learner</th>
                      <th className={thClass}>Email</th>
                      <th className={thClass}>Status</th>
                      <th className={thClass} />
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((r) => (
                      <tr key={r.id}>
                        <td className={tdClass}>{r.course ?? '—'}</td>
                        <td
                          className={`${tdClass} whitespace-nowrap text-gray-bold`}
                        >
                          {r.number}
                        </td>
                        <td className={tdClass}>{r.category ?? '—'}</td>
                        <td className={`${tdClass} whitespace-nowrap`}>
                          {fmtDateTime(r.start)}
                        </td>
                        <td className={tdClass}>{r.learner ?? '—'}</td>
                        <td className={tdClass}>{r.email ?? '—'}</td>
                        <td className={tdClass}>
                          <Pill value={r.status} />
                        </td>
                        <td className={`${tdClass} text-right`}>
                          {['Booked', 'Waiting List'].includes(
                            r.status ?? '',
                          ) &&
                            r.start &&
                            new Date(r.start) > new Date() && (
                              <button
                                className={btnGhost}
                                disabled={release.isPending}
                                onClick={() => release.mutate(r.id)}
                              >
                                Release seat
                              </button>
                            )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <Pagination
                page={data.page}
                pages={Math.ceil(data.total / data.pageSize)}
                total={data.total}
                onPage={setPage}
              />
            </Card>
          </>
        )}
      </PageBody>
    </>
  );
};

export default AdminRegistrations;
